<script>
	import { watch, activeElement } from 'runed'
	import { ChevronUp, ChevronDown } from 'lucide-svelte'

	let { 
		value = $bindable(""),
		step = 1,
		name,
		min = -Infinity,
		max = Infinity,
		precision = 3,
    required = true,
		showValueHint = true,
		showMinMaxHints = false,
		...inputProps
	} = $props()
	
  let container = $state(null)
  let input = $state(null)
	let query = $state(value || "0")
	let safeQuery = $derived(String(query).replaceAll(',', '.'))
	let delimeter = (1.1).toLocaleString(navigator.language).substring(1, 2)
	let internalUpdate = $state(false)
  let hasFocus = $derived(container?.contains(activeElement.current))

  let containerClass = $derived(hasFocus ? "input-container group ring-2 ring-blue-600" : "input-container group")
	let buttonClass = $derived(hasFocus ? "absolute right-0 h-full flex flex-col opacity-100" : "absolute right-0 h-full flex flex-col opacity-0")

	let allowedKeys = ["0","1","2","3","4","5","6","7","8","9",".", ",", "/"]

	let validFractionalInput = $derived(value && (safeQuery != value))

	// Handle null values for min/max
	let trueMin = $derived(min === null ? -Infinity : min)
	let trueMax = $derived(max === null ? Infinity : max)

	let hints = $derived.by(() => {
		let array = []
		if (showValueHint && validFractionalInput) array.push(`Valeur: ${value}`)
		if (showMinMaxHints) {
			if (![null, undefined, false].includes(min)) array.push(`Min: ${min}`)
			if (![null, undefined, false].includes(max)) array.push(`Max: ${max}`)
		}
		return array
	})
	
	watch(() => value, () => {
		if (!internalUpdate) {
			setQueryFromValue()
		}
    input.setCustomValidity(value === "" ? "Ce nombre n'est pas valide" : "")
		internalUpdate = false
	})
	
	function handleKeydown(e) {
		const key = e.key
		
		// Allow standard keys
		if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key)) return

		// Allow ctrl/meta
		if (e.ctrlKey || e.metaKey) return

		// Handle increment
		if (key === "ArrowUp") return handleIncrement(e)

		// Handle decrement
		if (key === "ArrowDown") return handleDecrement(e)

		// Prevent if not in safe list
		if (!allowedKeys.includes(key)) return e.preventDefault()
	}

	function handleInput(e) {
		internalUpdate = true
		value = formatNumber()
	}

	function setQueryFromValue() {
		query = String(value).replaceAll(".", delimeter).replaceAll(",", delimeter)
	}

	function formatNumber() {
		if (query === "") return ""

		if (query.includes("/")) {
			
			// If there are more than one slashes this is invalid
			if ((query.match(/\//g) || []).length > 1) {
				return null
			}

			// Split the numbers
			const [first, second] = query.split("/").map(Number)

			// If this leaves valid numbers and not divide by zero then return the result
			if (!isNaN(first) && !isNaN(second) && second !== 0) {
				return roundToPrecision(first / second)
			// Else return invalid
			} else {
				return ""
			}
		}

		// Else once fractions dealt with if it's not a valid number return invalid
		if (isNaN(safeQuery)) return ""

		// If we have got this far, return the float value of the query
		return parseFloat(safeQuery, 10)
	}

	function roundToPrecision(number) {
		return +number.toFixed(precision)
	}

	function handleIncrement(e) {
		e.preventDefault()
		const startNumber = formatNumber() || 0
		if (isNaN(safeQuery)) return
		const desiredStep = e.shiftKey ? (step * 10) : e.altKey ? (step / 10) : step
		const nextValue = Math.min(roundToPrecision(startNumber + desiredStep), trueMax)
		value = nextValue
	}

	function handleDecrement(e) {
		e.preventDefault()
		const startNumber = formatNumber() || 0
		if (isNaN(safeQuery)) return
		const desiredStep = e.shiftKey ? (step * 10) : e.altKey ? (step / 10) : step
		const nextValue = Math.max(roundToPrecision(startNumber - desiredStep), trueMin)
		value = nextValue
	}
	
</script>

<!-- A container for the input and buttons -->
<!-- <div class="relative w-full border border-gray-200 h-[2.5rem] group"> -->

<div class={containerClass} bind:this={container}>

	<!-- The query input which triggers the formatted value to be set -->
	<input 
		bind:this={input}
		bind:value={query}
    {required}
		onkeydown={handleKeydown}
		oninput={handleInput}
		{...inputProps}
		class="input absolute top-0 left-0 w-full h-full focus:outline-none text-sm" 
	/>

	<!-- The button area -->
	<div class={buttonClass}>

		<!-- Increment and decrement buttons -->
		<button type="button" class="bg-gray-100 h-full px-1 hover:bg-gray-200 cursor-pointer" tabindex="-1" onclick={handleIncrement}>
			<ChevronUp size={14} />
		</button>
		<button type="button" class="bg-gray-100 h-full px-1 hover:bg-gray-200 cursor-pointer" tabindex="-1" onclick={handleDecrement}>
			<ChevronDown size={14} />
		</button>
		
	</div>
	
</div>

<!-- Show any desired hints -->
{#if hints.length > 0}
  <p class="text-[0.7rem] text-gray-500 mt-1 flex gap-1">
    {#each hints as hint, idx (hint)}
      <span>{hint}</span>
      {#if idx < hints.length - 1}
        <span>|</span>
      {/if}
    {/each}
  </p>
{/if}

<!-- If a name prop is provided include a hidden input with the bound value (for forms) -->
{#if name}
	<input type="hidden" bind:value {name} />
{/if}
